import { ComponentProps, ReactNode, useCallback, useMemo } from 'react';

import { StoreItem } from '@givebacks/api/store';
import { centsToDollar } from '@givebacks/utils/currency';
import { Icon, IconMore } from 'ui-icons';

import { ButtonProps } from '../../../../Buttons';
import { Indicator, ThemeIcon, ThemeIconProps } from '../../../../DataDisplay';
import { ImageMantine } from '../../../../DataDisplay/Image';
import { AspectRatio } from '../../../../Layout';
import { Center } from '../../../../Layout/Center';
import { Group } from '../../../../Layout/Group';
import { Stack } from '../../../../Layout/Stack';
import { Text } from '../../../../Typography/Text/Text';
import { DEFAULT_IMAGE_PLACEHOLDER } from '../../../../config';
import { KebabMenu, KebabMenuProps } from '../../KebabMenu';
import { QuantitySelector, QuantitySelectorProps } from '../../buttons';
import classes from './Cart.module.css';

interface CartItemOptions extends KebabMenuProps {}
type CartItemProps = {
  itemType?: StoreItem['__type'];
  imgSrc?: string;
  name?: string;
  onNameClick?: () => void;
  description?: ReactNode;
  total?: number;
  actionsSize?: ButtonProps['size'];
  onRemove?: () => void;
  itemId: string;
  quantity?: { value: number; minValue?: number; maxValue?: number };
  onQuantityChange?: ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) => void;
  variablePriced?: boolean;
  showQuantitySelector?: boolean;
  showQuantityIndicator?: boolean;
  onClick?: (itemId: string) => void;
  showHasMoreOptions?: boolean;
  Icon?: Icon;
  iconProps?: ThemeIconProps;
} & CartItemOptions;

const CartItemAction = ({
  size,
  quantity,
  visible,
  onChange,
}: {
  type?: StoreItem['__type'];
  size?: ButtonProps['size'];
  quantity: { value: number; minValue?: number; maxValue?: number };
  visible?: boolean;
  onChange?: QuantitySelectorProps['onChange'];
}) => {
  if (!visible) return null;

  return (
    <QuantitySelector
      size={size}
      color="gray"
      value={quantity.value}
      maxValue={quantity.maxValue}
      minValue={quantity.minValue}
      onChange={onChange}
    />
  );
};

const CartItemImage = ({
  imgSrc,
  Icon,
  iconProps,
  onClick,
}: {
  imgSrc?: string;
  Icon?: Icon;
  iconProps?: ThemeIconProps;
  onClick?: () => void;
}) => {
  const _iconProps = useMemo(() => {
    return {
      ...iconProps,
      c: iconProps?.c ?? 'gray.4',
      color: iconProps?.color ?? 'gray.1',
    };
  }, [iconProps]);

  const props = useMemo(() => {
    let props: ComponentProps<typeof AspectRatio> = {
      className: classes.item_image,
    };

    if (onClick) {
      props = {
        ...props,
        onClick: onClick,
        role: 'link',
        style: { cursor: 'pointer' },
      };
    }

    return props;
  }, [onClick]);

  return (
    <AspectRatio {...props}>
      {Icon ? (
        <Center>
          <ThemeIcon variant="filled" {..._iconProps} size="100%" radius="sm">
            <Icon size={32} />
          </ThemeIcon>
        </Center>
      ) : (
        <ImageMantine src={imgSrc ?? DEFAULT_IMAGE_PLACEHOLDER} />
      )}
    </AspectRatio>
  );
};

const CartItem = ({
  imgSrc,
  name,
  onNameClick,
  description,
  total,
  quantity,
  actionsSize,
  items,
  itemId,
  withinPortal,
  variablePriced,
  onQuantityChange,
  showQuantitySelector = true,
  showQuantityIndicator,
  onClick,
  showHasMoreOptions,
  Icon,
  iconProps,
}: CartItemProps) => {
  const itemOptions = useMemo(
    () => items?.map((item) => ({ ...item, data: { ...item.data, uuid: itemId } })),
    [itemId, items]
  );

  const handleQuantityChange = useCallback(
    (quantity: number) => {
      onQuantityChange?.({ cartItemId: itemId, quantity: quantity });
    },
    [itemId, onQuantityChange]
  );

  const nameTextProps = useMemo(() => {
    let props: ComponentProps<typeof Text<'a'>> = {
      fz: 'sm',
      fw: 500,
      style: { wordBreak: 'break-word' },
    };

    if (onNameClick) {
      props = {
        ...props,
        onClick: onNameClick,
        component: 'a',
        style: {
          ...props.style,
          cursor: 'pointer',
        },
      };
    }

    return props;
  }, [onNameClick]);

  return (
    <Group
      className={classes.item}
      wrap="nowrap"
      onClick={() => onClick?.(itemId)}
      mod={{ clickable: !!onClick }}
    >
      <Indicator
        size={21}
        offset={4}
        withBorder
        position="top-start"
        disabled={!showQuantityIndicator}
        label={quantity?.value}
      >
        <CartItemImage imgSrc={imgSrc} Icon={Icon} iconProps={iconProps} onClick={onNameClick} />
      </Indicator>

      <Stack className={classes.item_content}>
        {/* details */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Text {...nameTextProps}>{name}</Text>
            <Text fz="xs" c="gray" style={{ wordBreak: 'break-word' }}>
              {description}
            </Text>
          </Stack>
          {total && (
            <Stack gap={4}>
              <Text fz="sm" fw={600}>
                {centsToDollar(Number(total))}
              </Text>
            </Stack>
          )}
        </Group>
        {/* cart item actions */}
        <Group mt="auto" justify="space-between">
          <Center>
            <CartItemAction
              size={actionsSize}
              quantity={quantity ?? { value: 1 }}
              visible={showQuantitySelector && variablePriced != true}
              onChange={handleQuantityChange}
            />
          </Center>
          {/* TODO: (Ui): Update KebabMenu to allow for destructive styles @acdodd17 */}
          {showHasMoreOptions ? (
            <ThemeIcon size={24} radius="xs" color="gray" variant="gray">
              <IconMore size={12} />
            </ThemeIcon>
          ) : (
            <KebabMenu
              withinPortal={withinPortal}
              buttonProps={{ size: actionsSize, color: 'gray', variant: 'gray' }}
              items={itemOptions}
            />
          )}
        </Group>
      </Stack>
    </Group>
  );
};

export { CartItem };
export type { CartItemProps };
